// Boss state machines (spec-bosses): while an enemy carries the `boss` marker,
// this per-tick hook drives its attack cycle — bullet volleys fire here and
// attack phases override the default pursuit steering (maw coils, razor
// lunges). Driven from updateEnemies for entities with `e.boss`; a null
// return means "default pursuit this tick". Deterministic: phase clocks run
// on the sim tick (boss.phase_tick) and volley angles derive from bullet
// indices — no RNG, no wall clock.

import type { WorldState } from './state.js';
import type { Enemy, Vec2 } from '../types.js';
import { wrappedDelta } from './collision.js';
import { ABYSS_MAW_ID, VOID_RAZOR_ID, findEnemyDef } from './director.js';
import { spawnEnemyBullet } from './weapon-system.js';

/** Steering override while a boss is in an attack phase. */
export interface BossControl {
  desired: Vec2;
  speedMult: number;
}

// Abyss Maw cycle (60 Hz ticks): HUNT 150 → TELEGRAPH 30 → RADIAL BURST
// (volley on entry, second volley +34, phase ends +60) → HUNT.
const MAW_HUNT_TICKS = 150;
const MAW_TELEGRAPH_TICKS = 30;
const MAW_VOLLEY_GAP_TICKS = 34;
const MAW_BURST_TICKS = 60;
const MAW_RING_COUNT = 16;
const MAW_RING_COUNT_ENRAGED = 24;
const MAW_BULLET_SPEED = 32;
// Enrage (hp ≤ 60%): the whole cycle shortens by 40%.
const MAW_ENRAGE_SCALE = 0.6;

// Void Razor cycle: STALK 90 → AIMED FAN (instant volley) → LUNGE → STALK.
const RAZOR_STALK_TICKS = 90;
const RAZOR_LUNGE_TICKS = 50;
const RAZOR_LUNGE_TICKS_ENRAGED = 70;
const RAZOR_FAN_COUNT = 3;
const RAZOR_FAN_COUNT_ENRAGED = 5;
/** Total fan spread in degrees across the volley. */
const RAZOR_FAN_SPREAD_DEG = 20;
const RAZOR_BULLET_SPEED = 36;
const RAZOR_BULLET_SPEED_ENRAGED = 40;
const RAZOR_LUNGE_SPEED_MULT = 3.0;
/** Fan aim lead: shoot where the player will be 0.8 s from now. */
const RAZOR_AIM_LEAD_S = 0.8;

/** Coiling in place: zero desired velocity brakes the boss at max_force. */
const BRAKE: BossControl = { desired: { x: 0, y: 0 }, speedMult: 1 };

/** Enrage: 60% health gone, or the time-based fuse from the director. */
function isEnraged(e: Enemy, tick: number): boolean {
  const boss = e.boss!;
  return e.hp * 5 <= e.max_hp * 3 || tick >= boss.enrage_at_tick;
}

function normalize(v: Vec2): Vec2 {
  const l = Math.sqrt(v.x * v.x + v.y * v.y);
  if (l < 1e-8) return { x: 0, y: 1 };
  return { x: v.x / l, y: v.y / l };
}

/**
 * Advance the boss's attack cycle one tick and fire any volleys due. Returns
 * the steering override for attack phases (maw telegraph/burst, razor lunge)
 * or null while the boss should keep its default pursuit (hunt/stalk).
 */
export function updateBossAI(e: Enemy, world: WorldState, tick: number): BossControl | null {
  const boss = e.boss;
  if (!boss || !world.player.alive) return null;
  switch (e.def_id) {
    case ABYSS_MAW_ID:
      return updateMaw(e, world, tick);
    case VOID_RAZOR_ID:
      return updateRazor(e, world, tick);
    default:
      return null; // Unknown boss def: plain pursuit, no bullet patterns.
  }
}

/** Abyss Maw: a tank that telegraphs, then spits bullet rings. */
function updateMaw(e: Enemy, world: WorldState, tick: number): BossControl | null {
  const boss = e.boss!;
  const enraged = isEnraged(e, tick);
  const huntTicks = Math.round(MAW_HUNT_TICKS * (enraged ? MAW_ENRAGE_SCALE : 1));
  const telegraphTicks = Math.round(MAW_TELEGRAPH_TICKS * (enraged ? MAW_ENRAGE_SCALE : 1));
  const volleyGap = Math.round(MAW_VOLLEY_GAP_TICKS * (enraged ? MAW_ENRAGE_SCALE : 1));
  const burstTicks = Math.round(MAW_BURST_TICKS * (enraged ? MAW_ENRAGE_SCALE : 1));
  const elapsed = tick - boss.phase_tick;

  switch (boss.phase_index) {
    case 0: // HUNT: default pursuit until the cycle clock expires.
      if (elapsed >= huntTicks) {
        boss.phase_index = 1;
        boss.phase_tick = tick;
        world.events.emit(
          'boss_telegraph',
          { enemy_id: e.id, def_id: e.def_id, until: tick + telegraphTicks, pos: { x: e.pos.x, y: e.pos.y } },
          tick
        );
      }
      return null;
    case 1: // TELEGRAPH: coil in place, no fire — the burst is coming.
      if (elapsed >= telegraphTicks) {
        boss.phase_index = 2;
        boss.phase_tick = tick;
        fireMawRing(e, world, tick, enraged, 0);
      }
      return BRAKE;
    case 2: // RADIAL BURST: second volley mid-phase, then back to HUNT.
      if (elapsed === volleyGap) {
        fireMawRing(e, world, tick, enraged, 1);
      }
      if (elapsed >= burstTicks) {
        boss.phase_index = 0;
        boss.phase_tick = tick;
        return null;
      }
      return BRAKE;
    default: // Corrupt phase: restart the cycle rather than soft-locking.
      boss.phase_index = 0;
      boss.phase_tick = tick;
      return null;
  }
}

/** Radial ring: `count` bullets evenly spaced; later volleys interleave. */
function fireMawRing(e: Enemy, world: WorldState, tick: number, enraged: boolean, volley: number): void {
  const def = findEnemyDef(e.def_id);
  const count = enraged ? MAW_RING_COUNT_ENRAGED : MAW_RING_COUNT;
  // Each successive volley rotates half a step so the rings interleave.
  const offset = volley * (Math.PI / count);
  for (let i = 0; i < count; i++) {
    const angle = offset + (i / count) * Math.PI * 2;
    spawnEnemyBullet(world, e, def, { x: Math.cos(angle), y: Math.sin(angle) }, MAW_BULLET_SPEED, tick);
  }
}

/** Void Razor: a duelist that aims a fan at the player's future position, then lunges. */
function updateRazor(e: Enemy, world: WorldState, tick: number): BossControl | null {
  const boss = e.boss!;
  const enraged = isEnraged(e, tick);
  const lungeTicks = enraged ? RAZOR_LUNGE_TICKS_ENRAGED : RAZOR_LUNGE_TICKS;
  const elapsed = tick - boss.phase_tick;

  switch (boss.phase_index) {
    case 0: // STALK: default intercept pursuit until the cycle clock expires.
      if (elapsed >= RAZOR_STALK_TICKS) {
        boss.phase_index = 1;
        boss.phase_tick = tick;
      }
      return null;
    case 1: // AIMED FAN: one volley at the predicted position, then lunge.
      fireRazorFan(e, world, tick, enraged);
      boss.phase_index = 2;
      boss.phase_tick = tick;
      return null;
    case 2: // LUNGE: 2.5× speed straight at the player.
      if (elapsed >= lungeTicks) {
        boss.phase_index = 0;
        boss.phase_tick = tick;
        return null;
      }
      return { desired: normalize(wrappedDelta(world.player.pos, e.pos)), speedMult: RAZOR_LUNGE_SPEED_MULT };
    default: // Corrupt phase: restart the cycle rather than soft-locking.
      boss.phase_index = 0;
      boss.phase_tick = tick;
      return null;
  }
}

/** Aimed fan: `count` bullets across a 20° fan at the predicted position. */
function fireRazorFan(e: Enemy, world: WorldState, tick: number, enraged: boolean): void {
  const def = findEnemyDef(e.def_id);
  const player = world.player;
  // Predicted position: pos + vel × 0.8 s, seam-aware shortest path.
  const predicted = {
    x: player.pos.x + player.vel.x * RAZOR_AIM_LEAD_S,
    y: player.pos.y + player.vel.y * RAZOR_AIM_LEAD_S,
  };
  const to = wrappedDelta(predicted, e.pos);
  const baseAngle = Math.atan2(to.y, to.x);
  const count = enraged ? RAZOR_FAN_COUNT_ENRAGED : RAZOR_FAN_COUNT;
  const speed = enraged ? RAZOR_BULLET_SPEED_ENRAGED : RAZOR_BULLET_SPEED;
  const spreadRad = (RAZOR_FAN_SPREAD_DEG * Math.PI) / 180;
  // Symmetric fan around the aim direction (ally volley-fan pattern).
  for (let i = 0; i < count; i++) {
    const angle = baseAngle + ((i - (count - 1) / 2) / (count - 1)) * spreadRad;
    spawnEnemyBullet(world, e, def, { x: Math.cos(angle), y: Math.sin(angle) }, speed, tick);
  }
}
